import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type { ApiError } from '@app/api/ApiError';
import { request, TIMEOUT_ORDER_LONGAMS } from '@app/api/client';

/**
 * Superfície `/debug` do backend — **fora** do contrato `/api/v1`, que é
 * congelado. O consumidor é a página oculta `/comandos` (28/08), só de admin. O
 * card Tenant de T21 deixou de sondá-la (aim-gl6): o botão «Zerar dados de
 * demonstração» saiu em 28/08, e a sonda ficou sem uso e deixava um 404 no lab.
 *
 * As rotas podem não existir: o backend só as monta com `POC_AUTH_DEV=1` e/ou
 * `POC_COMANDOS_OCULTOS=1`. A sondagem `GET /debug/ping` é o que diz quais
 * comandos oferecer — 404 (ou qualquer erro) significa «não existe», e a tela
 * não mostra nada.
 */

export const debugKeys = {
  ping: ['debug', 'ping'] as const,
  elegiveisCru: ['debug', 'restaurar-cru', 'elegiveis'] as const,
};

/** `data` de `GET /debug/ping`. */
export interface DebugPing {
  /** `dev` (POC_AUTH_DEV) ou `comandos` (só os comandos ocultos). */
  environment: string;
  /** `POST /debug/reset` existe. */
  reset: boolean;
  /** `POST /debug/restaurar-cru` existe. */
  restaurar_cru: boolean;
}

/**
 * Um passo da limpeza no executor (`data.passos[]`, 29/08).
 *
 * `ok` é o desfecho **do objetivo**, não da chamada: «não havia contrato ativo»
 * chega como `ok: true` com `codigo` preenchido, porque o estado desejado já
 * valia. Repetir o restauro num executor já limpo não pinta a tela de vermelho.
 */
export interface ExecutorStep {
  /** Slug da identidade, `politicas`, ou `executor` (reset completo, §13). */
  target: string;
  /** Ordem do contrato: `contract.remove` | `policy.publish` | `executor.reset`. */
  action: string;
  ok: boolean;
  detail: string;
  /** Código do executor quando houve um — inclusive nos passos `ok`. */
  code?: string;
}

/** `data` de `POST /debug/restaurar-cru` (comandos ocultos, 28/08). */
export interface RestoreRawResult {
  /** Linhas removidas por tabela — inclusive as que já estavam em zero. */
  deleted: Record<string, number>;
  /** Tabelas esvaziadas. */
  emptied: string[];
  /** State cleared or configuration restored to factory defaults. */
  cleaned: string[];
  /** O que ficou de pé. */
  preserved: string[];
  /** URL do executor configurada no tenant. */
  executor_url: string;
  /** A metade remota rodou (29/08). Falso = o executor não foi tocado. */
  executor_touched: boolean;
  /** Passos do executor, na ordem em que rodaram. Vazio, nunca nulo. */
  steps: ExecutorStep[];
  /**
   * Relatório do reset completo do executor (§13, 04/09), como o executor o
   * devolve — só quando o corpo pediu `reset` e o passo ficou `ok`.
   */
  executor_reset?: ExecutorResetReport | null;
  executed_at: string;
}

/**
 * O `estado` do §13 (`POST /api/v1/reset-raw`): o que sumiu no executor, por
 * tabela **dele** (nomes do executor, não do produto), e o que ficou.
 */
export interface ExecutorResetReport {
  deleted: Record<string, number>;
  /** Contadores reiniciados — o próximo contrato nasce `#1`, não `#458`. */
  sequences_reset: string[];
  pdp_modules_removed: string[];
  collector_cleared: number;
  preserved: string[];
}

/** Uma linha de `GET /debug/restaurar-cru/elegiveis`. */
export interface SlugEligible {
  slug: string;
  /**
   * Tri-estado: `true` há contrato ativo · `false` não há · `null` **não lido**
   * (`error_code` diz por quê). A tela não escreve «sem contrato» sobre um
   * executor que ninguém conseguiu ler.
   */
  has_contract: boolean | null;
  /** Id do contrato ativo no executor; nulo sem contrato ou sem leitura. */
  contract: number | null;
  /** O executor conhece a aplicação. */
  in_executor: boolean;
  error_code?: string;
}

/** `data` de `GET /debug/restaurar-cru/elegiveis` (29/08). */
export interface EligibleRaw {
  slugs: SlugEligible[];
  executor_url: string;
  /**
   * A limpeza do executor está liberada agora — guard do lab inerte ou
   * `POC_LAB_MODO_DEMO=1`. Falso, a tela mostra o bloco desabilitado com o
   * motivo em vez de deixar o botão colher um 502.
   */
  released: boolean;
  /** Código quando o executor inteiro não foi lido. */
  error_code?: string;
  read_at: string;
}

/**
 * Corpo **aditivo** de `POST /debug/restaurar-cru` (29/08).
 *
 * Duas formas, e a tela manda uma **ou** outra: `{slugs, policies}` revoga slug
 * a slug; `{reset: true}` (§13, 04/09) pede o reset completo do executor. Mandar
 * `reset` junto de `slugs`/`policies` é `422 dados_invalidos` no backend.
 */
export interface ExecutorRequest {
  slugs?: string[];
  policies?: boolean;
  reset?: boolean;
}

/**
 * Sonda `GET /debug/ping`. Devolve `null` quando a superfície não existe (404 em
 * produção) — nunca erro: a ausência é resposta, não falha.
 */
export function useDebugPing(): UseQueryResult<DebugPing | null, ApiError> {
  return useQuery<DebugPing | null, ApiError>({
    queryKey: debugKeys.ping,
    queryFn: async () => {
      try {
        return await request<DebugPing>({ method: 'GET', url: '/debug/ping' });
      } catch {
        return null;
      }
    },
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * `POST /debug/restaurar-cru` — devolve o tenant ao estado de recém-criado.
 *
 * Depois do sucesso, limpa também o que **não** está no banco: as posições que o
 * mapa de acesso (T19) guarda por navegador em `localStorage`. Elas são de nós
 * que acabaram de deixar de existir; sem isso o próximo mapa reabriria com o
 * arrasto de uma jornada apagada.
 */
export function useRestoreRaw(): UseMutationResult<
  RestoreRawResult,
  ApiError,
  ExecutorRequest | undefined
> {
  const queryClient = useQueryClient();
  return useMutation<RestoreRawResult, ApiError, ExecutorRequest | undefined>({
    // Apaga a jornada inteira, tabela por tabela, e antes disso revoga no executor
    // o que a tela marcou: 2 min, não os 20 s do cliente.
    //
    // O corpo é **omitido** quando não há nada a fazer no executor, e não mandado
    // vazio: é o que mantém a rota aditiva de verdade — o backend sem o campo se
    // comporta exatamente como no dia em que ela não existia.
    mutationFn: (executor) =>
      request<RestoreRawResult>({
        method: 'POST',
        url: '/debug/restaurar-cru',
        timeout: TIMEOUT_ORDER_LONGAMS,
        data: executor ? { executor } : undefined,
      }),
    onSuccess: () => {
      try {
        window.localStorage.removeItem(MAP_POSICOES);
      } catch {
        // Storage indisponível (aba anônima, política de site): nada a limpar.
      }
      void queryClient.invalidateQueries();
    },
  });
}

/**
 * Chave do `localStorage` onde T19 guarda as posições arrastadas
 * (`useGraphLayout.STORAGE_KEY`). Repetida aqui de propósito: importar a tela do
 * mapa a partir de um hook de API acoplaria a camada de dados a um módulo de
 * interface, e um teste afirma que as duas continuam iguais.
 */
export const MAP_POSICOES = 'aim.accessmap.positions';

/**
 * `GET /debug/restaurar-cru/elegiveis` — os slugs que o comando aceita limpar no
 * executor, com o estado de cada um lá.
 *
 * A lista é do backend, não da tela: as três regras (allowlist do guard do lab,
 * agente conhecido da PoC ou do executor, nunca MCP) dependem de dados que o
 * frontend não tem. Sem a superfície `/debug` monta-se `null`, como no `ping`.
 *
 * `staleTime: 0` de propósito: o estado do contrato no executor muda a cada
 * aprovação, e a tela abre para decidir sobre ele.
 */
export function useEligibleRaw(
  habilitado: boolean,
): UseQueryResult<EligibleRaw | null, ApiError> {
  return useQuery<EligibleRaw | null, ApiError>({
    queryKey: debugKeys.elegiveisCru,
    queryFn: async () => {
      try {
        return await request<EligibleRaw>({
          method: 'GET',
          url: '/debug/restaurar-cru/elegiveis',
        });
      } catch {
        return null;
      }
    },
    enabled: habilitado,
    staleTime: 0,
    retry: false,
  });
}
